// core
import React, { ReactNode, useMemo } from 'react';

// apollo
import { useQuery } from '@apollo/client';
import carsFilterOptionsQuery from '../graphql/CarsFilterOptionsQuery.graphql';
import {
  CarsFilterOptionsQuery,
  CarsFilterOptionsQueryVariables
} from '../graphql/CarsFilterOptionsQuery';

// components
import { LoadingDiv } from 'components/LoadingDiv';

// material
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

// partials
import { CarFilterCheckbox } from './CarFilterCheckbox';
import { ParameterWithOptions } from '../../../globalTypes';

export interface Props {
  name: string;
  parameter: ParameterWithOptions;
  label: ReactNode;
  className: string;
  queryRes?: (q: any) => void;
}

const useStyles = makeStyles(() => ({
  root: {
    marginTop: 8
  },
  label: {
    marginLeft: -8
  },
  checkbox: {
    padding: 4,
    marginRight: 8
  }
}));

export const groups = {
  fuel: {
    gasoline: {
      label: 'Benzín',
      options: ['1', '3', '4', '5', '7']
    },
    diesel: {
      label: 'Diesel',
      options: ['2', '8']
    },
    electricity: {
      label: 'Elektromotor',
      options: ['5', '6', '8']
    }
  },

  gearbox: {
    manual: {
      label: 'Manuálna',
      options: ['1', '2', '3', '4', '5']
    },
    automatic: {
      label: 'Automatická',
      options: ['6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16']
    }
  }
} as const;

const groupValues = (
  parameter: ParameterWithOptions,
  data: readonly { id: string; label: string }[]
) => {
  let optionsConfig: Record<string, { label: string; options: readonly string[] }> | null = null;

  if (parameter === ParameterWithOptions.FUEL) {
    optionsConfig = groups.fuel;
  } else if (parameter === ParameterWithOptions.GEARBOX) {
    optionsConfig = groups.gearbox;
  }

  if (optionsConfig) {
    return Object.keys(optionsConfig)
      .filter((key) => data.find((option) => ~optionsConfig![key].options.indexOf(option.id)))
      .map((key) => ({
        label: optionsConfig![key].label,
        id: key
      }));
  }

  return data;
};

const Content = ({ name, parameter, queryRes }: Pick<Props, 'name' | 'parameter' | 'queryRes'>) => {
  const classes = useStyles();

  const {
    data: originalData,
    error,
    loading
  } = useQuery<CarsFilterOptionsQuery, CarsFilterOptionsQueryVariables>(carsFilterOptionsQuery, {
    variables: { parameter }
  });

  const data = useMemo(
    () => originalData && groupValues(parameter, originalData.parameterValues),
    [originalData, parameter]
  );

  if (loading) {
    return <LoadingDiv />;
  }

  if (data && queryRes) {
    queryRes(data);
  }

  if (error) {
    console.log(error);
    global.bugsnagClient.notify(error);
    return (
      <Typography variant="caption" color="error">
        Chyba v spojení so serverom. Skúste to prosím znova.
      </Typography>
    );
  }

  const items = (data || []).map((field) => (
    <React.Fragment key={field.id}>
      <CarFilterCheckbox name={name} label={field.label} value={field.id} />
      <br />
    </React.Fragment>
  ));
  const length = items.length;
  const itemsInCol = Math.ceil(length / 2);

  const cols = [];
  for (let c = 0; c < length; c += itemsInCol) {
    cols.push(
      <Grid item xs={6} key={c}>
        {items.splice(0, itemsInCol)}
      </Grid>
    );
  }

  return (
    <Grid container spacing={0} className={classes.root}>
      {cols}
    </Grid>
  );
};

export const CarsFilterCheckboxes = ({ label, name, parameter, className, queryRes }: Props) => {
  return (
    <div className={className}>
      <Typography variant="subtitle2" component="h2">
        {label}
      </Typography>

      <Content name={name} parameter={parameter} queryRes={queryRes} />
    </div>
  );
};
